import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from "redux-form";
import qs from 'query-string';

import Page from '../../pages/Page';
import PageHeader from '../../modules/PageHeader';
import PageMeta from '../../modules/PageMeta';
import PageSection from '../../modules/PageSection';
import Input from "../../modules/Input";
import Button from "../../modules/Button";
import ServiceSection from "../ServiceSection";

import * as mfaActions from '../../../actions/mfa';

import introText from '../../../services/ui/authenticationIntros';
import { selectSessionUserId } from '../../../selectors/users';
import { SSO_ID } from '../../../constants/services';

import styles from './styles.scss';
import ReferrerLink from "../../modules/ServiceReferrerLink";

class Authenticator extends Component {
    constructor(props){
        super(props);

        this.state = {
            primaryAuthMethod: {},
            currentAuthMethod: null,
            showSelectAlternate2fa: false,
            serviceId: null,
            token: null
        }

        this._verifyCode = this._verifyCode.bind(this);
        this._goBackToService = this._goBackToService.bind(this);
    }

    componentDidMount() {
        const queryString = qs.parse(this.props.location.search);
        this.setState({
            serviceId: queryString.serviceId,
            token: queryString.token
        });

        return this.props.getUserAuthMethods(queryString.token)
            .then(() => {
                const initialAuthMethod = Object.keys(this.props.authMethods).length > 0 ? this.props.authMethods.find(authMethod => authMethod.is_primary) : {};
                return this.setState({
                    primaryAuthMethod: initialAuthMethod,
                    currentAuthMethod: initialAuthMethod.method
                });
            })
            .then(() => {
                return this._initiate2fa(queryString.token);
            });
    }

    _goBackToService(){
        if(this.props.authSuccess){
            const queryString = qs.parse(this.props.location.search);
            let redirectTo = this.props.serviceReferrer.service_url + '?2fa=success';

            // if there is a redirect query string, we need to append it.
            if (queryString.redirect) {
                redirectTo = redirectTo + '&redirect=' + queryString.redirect
            }

            window.location = redirectTo;
        }
    }

    _initiate2fa(token){
        switch(this.state.currentAuthMethod){
            case 'sms':
                this.props.sendSmsCode(token);
                break;
            case 'email':
                this.props.sendEmailCode(token);
                break;
            default:
                return null;
        }
    }

    _showAlternateMethod(e){
        e.preventDefault();
        this.props.resetErrors();
        this.setState({
            showSelectAlternate2fa: true
        });
    }

    _changeToMethod(e, method){
        e.preventDefault();
        this.setState({
            currentAuthMethod: method,
            showSelectAlternate2fa: false
        }, () => this._initiate2fa(this.state.token));
    }

    _reInitiate2fa(e){
        e.preventDefault();
        this.props.resetErrors();
        this.props.resetForm();
        this._initiate2fa(this.state.token);
    }

    _verifyCode(values){
        switch(this.state.currentAuthMethod){
            case 'app':
                return this.props.verifyAppCode(values, this.state.serviceId, this.state.token)
                    .then(this._goBackToService);
            case 'sms':
                return this.props.verifySmsCode(values, this.state.serviceId, this.state.token)
                    .then(this._goBackToService);
            case 'email':
                return this.props.verifyEmailCode(values, this.state.serviceId, this.state.token)
                    .then(this._goBackToService);
            default:
                return null;
        }
    }

    render() {
        const {
            authMethods,
            handleSubmit
        } = this.props;

        return (
            <Page>
                <PageMeta title="Authenticator" />

                {this.state.serviceId && (
                    <ServiceSection
                        serviceId={this.state.serviceId}
                    />
                )}

                <PageHeader>
                    <h1>Two-Factor Authentication</h1>
                    <p>This extra step proves it’s really you trying to sign in.</p>
                </PageHeader>

                {this.state.serviceId !== SSO_ID.toString() && this.props.serviceReferrer &&
                    <PageSection>
                        <ReferrerLink service={this.props.serviceReferrer} />
                    </PageSection>
                }

                <PageSection mainContent>
                    {authMethods.length > 1 && this.state.showSelectAlternate2fa ? (
                        <div className={styles['authpage-select-alternate']}>
                            <p>
                                Please select your preferred authentication method:
                            </p>
                            <ul>
                                {authMethods.map((method, i) => {
                                    let methodName = method.method
                                    switch(method.method){
                                        case 'email':
                                            methodName = 'Email';
                                            break;
                                        case 'sms':
                                            methodName = 'SMS';
                                            break;
                                        case 'app':
                                            methodName = 'Google Authenticator App';
                                            break;
                                        default:
                                            return null;
                                    }

                                    return (
                                        <li key={i}>
                                            <a href="#" onClick={e => this._changeToMethod(e, method.method)}>{methodName}</a>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    ) : (
                        <div className={styles['authpage-container']}>
                            {introText[this.state.currentAuthMethod]}

                            <form onSubmit={handleSubmit(this._verifyCode).bind(this)}>
                                <div className={[styles['verify_code_input']]}>
                                    <Field
                                        label="Enter Code"
                                        component={Input}
                                        id="verify_code"
                                        type="text"
                                        name="verify_code"
                                        meta={{error: this.props.authErrors ? this.props.authErrors.verify_code : '', touched: this.props.authErrors ? ('verify_code' in this.props.authErrors) : false}}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                >Verify</Button>
                                <div className={styles['remember-box']}>
                                    <Field label="Remember device for 60 days" component={Input} type="checkbox" name="remember_device" id="remember-device" />
                                </div>
                            </form>

                            {this.props.authSuccess === true && (
                                <p><strong>Successfully verified! You will be redirected in a few seconds.</strong></p>
                            )}


                            {(this.state.currentAuthMethod !== 'app' && !this.props.token_expired) && (
                                <p>
                                    <a href="#" onClick={e => this._reInitiate2fa(e)}>Didn't get a code? Click here to re-send.</a>
                                </p>
                            )}

                            {(authMethods.length > 1 && !this.props.token_expired) && (
                                <p>
                                    <a href="#" onClick={e => this._showAlternateMethod(e)}>Try another authentication method.</a>
                                </p>
                            )}
                        </div>
                    )}

                </PageSection>
            </Page>
        )
    }
}

const mapStateToProps = state => {
    return {
        ...state.mfa,
        sessionUserId: selectSessionUserId(state),
        serviceReferrer: state.services.serviceReferrer
    }
}

const mapDispatchToProps = dispatch => {
    return {
        getUserAuthMethods(token){
            return dispatch(mfaActions.getUserAuthMethods(token));
        },
        sendEmailCode(token){
            return dispatch(mfaActions.sendEmailCode(token));
        },
        verifyEmailCode(values, serviceId, token){
            return dispatch(mfaActions.verifyEmailCode(values, serviceId, token));
        },
        verifyAppCode(values, serviceId, token) {
            return dispatch(mfaActions.verifyAppCode(values, serviceId, token));
        },
        resetForm(){
            return dispatch(reset('authenticator'));
        },
        resetErrors(){
            return dispatch(mfaActions.resetAuthErrors());
        },
        sendSmsCode(token){
            return dispatch(mfaActions.sendSmsCode(token));
        },
        verifySmsCode(values, serviceId, token){
            return dispatch(mfaActions.verifySmsCode(values, serviceId, token));
        }
    }
}

Authenticator = connect(mapStateToProps, mapDispatchToProps)(Authenticator)

export default reduxForm({
    form: 'authenticator'
})(Authenticator);